import React from 'react';
import { Calendar } from 'lucide-react';
import { 
  DatePresetId, 
  DATE_PRESETS, 
  DateRangeFilter, 
  findPresetById, 
  getPresetLabel 
} from '../utils/datePresets';
import { GenericColumn } from '../utils/tableColumns';

interface DateRangeFilterPopoverProps <T> {
  column: GenericColumn<T>;
  filterPopoverRef: React.RefObject<HTMLDivElement>;
  dateRangeFilters: { [key: string]: DateRangeFilter };
  handleDateRangeFilterChange: (columnKey: string, type: 'startDate' | 'endDate', value: string) => void;
  clearDateRangeFilter: (columnKey: string) => void;
  applyPresetDateRange: (columnKey: string, presetId: DatePresetId) => void;
  setActiveFilter: (filter: string | null) => void;
}

const DateRangeFilterPopover = <T,>({ 
  column,
  filterPopoverRef,
  dateRangeFilters,
  handleDateRangeFilterChange,
  clearDateRangeFilter,
  applyPresetDateRange,
  setActiveFilter
}: DateRangeFilterPopoverProps<T>) => {
  if (!column.filterable) return null;
  
  const columnKey = column.key.toString();
  const currentFilter = dateRangeFilters[columnKey] || { startDate: null, endDate: null };
  
  // Check if current filter matches any preset
  const isPresetActive = (presetId: DatePresetId): boolean => {
    const preset = findPresetById(presetId);
    if (!preset) return false;
    
    const presetRange = preset.getDateRange();
    return presetRange.startDate === currentFilter.startDate && 
           presetRange.endDate === currentFilter.endDate;
  };

  return (
    <div 
      ref={filterPopoverRef}
      className="absolute top-full left-0 mt-1 z-40 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 p-2 min-w-[300px]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-2 border-b pb-1">
        <span className="text-sm font-medium flex items-center">
          <Calendar size={14} className="mr-1" /> 
          กรองช่วงวันที่
        </span>
        <button 
          className="text-xs text-blue-600 hover:text-blue-800"
          onClick={() => {
            clearDateRangeFilter(columnKey);
            setActiveFilter(null);
          }}
        >
          ล้าง
        </button>
      </div>

      <div className="mb-3">
        <div className="text-xs font-medium text-gray-500 mb-1">ช่วงเวลาที่กำหนดไว้แล้ว</div>
        <div className="grid grid-cols-3 gap-1">
          {DATE_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => applyPresetDateRange(columnKey, preset.id)}
              className={`px-2 py-1 text-xs rounded flex items-center justify-center 
                ${isPresetActive(preset.id) 
                  ? 'bg-blue-100 text-blue-700 font-medium' 
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'}`}
            >
              <span className="mr-1">{preset.icon}</span>
              {getPresetLabel(preset)}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t pt-2">
        <div className="text-xs font-medium text-gray-500 mb-1">กำหนดช่วงเวลาเอง</div>
        <div className="space-y-2 p-1">
          <div className="flex items-center">
            <label htmlFor={`${columnKey}-start-date`} className="block text-xs text-gray-500 w-24">
              วันที่เริ่มต้น
            </label>
            <input
              id={`${columnKey}-start-date`}
              type="date"
              value={currentFilter.startDate || ''}
              onChange={(e) => handleDateRangeFilterChange(columnKey, 'startDate', e.target.value)}
              className="flex-1 p-1 text-sm border border-gray-300 rounded"
            />
          </div>

          <div className="flex items-center">
            <label htmlFor={`${columnKey}-end-date`} className="block text-xs text-gray-500 w-24">
              วันที่สิ้นสุด
            </label>
            <input
              id={`${columnKey}-end-date`}
              type="date"
              value={currentFilter.endDate || ''}
              onChange={(e) => handleDateRangeFilterChange(columnKey, 'endDate', e.target.value)}
              className="flex-1 p-1 text-sm border border-gray-300 rounded"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-3">
        <button
          onClick={() => clearDateRangeFilter(columnKey)}
          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
        >
          ล้างตัวกรอง
        </button>
        <button
          onClick={() => setActiveFilter(null)}
          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          disabled={!currentFilter.startDate && !currentFilter.endDate}
        >
          นำไปใช้
        </button>
      </div>
    </div>
  );
};

export default DateRangeFilterPopover;
